import { useState, useEffect, useMemo } from "react";
import { Gift, RefreshCw, Loader2, Users, Clock, BadgePercent, ArrowRight, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const API = "/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const STATUS = {
  rewarded: { label: "Rewarded", cls: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  pending: { label: "Checkout not completed", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  failed: { label: "Credit failed", cls: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
};

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function Person({ p }) {
  return (
    <div className="min-w-0">
      <p className={`text-sm font-medium truncate ${p.deleted ? "italic text-muted-foreground" : ""}`}>{p.name || p.email || p.user_id}</p>
      {p.email && <p className="text-xs text-muted-foreground truncate">{p.email}</p>}
    </div>
  );
}

function ReferrersTable({ referrers }) {
  return (
    <div className="rounded-xl border border-border overflow-x-auto" data-testid="referrers-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Referrer</TableHead>
            <TableHead>Code</TableHead>
            <TableHead className="text-right">Rewarded referrals</TableHead>
            <TableHead className="text-right">Pending</TableHead>
            <TableHead className="text-right">Outstanding 50% months</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Last referral</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {referrers.map((r) => (
            <TableRow key={r.user_id} data-testid={`referrer-row-${r.user_id}`}>
              <TableCell><Person p={r} /></TableCell>
              <TableCell><code className="text-xs font-mono">{r.referral_code || "—"}</code></TableCell>
              <TableCell className="text-right font-semibold">{r.total_referrals}</TableCell>
              <TableCell className="text-right text-muted-foreground">{r.pending_checkouts || "—"}</TableCell>
              <TableCell className="text-right">
                {r.discounted_months_remaining > 0
                  ? <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" data-testid={`outstanding-months-${r.user_id}`}>{r.discounted_months_remaining}</Badge>
                  : <span className="text-muted-foreground">0</span>}
              </TableCell>
              <TableCell className="text-xs capitalize">{r.subscription_tier || "—"}{r.subscription_status && r.subscription_status !== "active" ? ` (${r.subscription_status})` : ""}</TableCell>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.last_referral_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ReferralLogTable({ referrals }) {
  return (
    <div className="rounded-xl border border-border overflow-x-auto" data-testid="referral-log-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Referrer</TableHead>
            <TableHead className="w-8" />
            <TableHead>New subscriber</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>New subscriber discount</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {referrals.map((r) => {
            const s = STATUS[r.status] || STATUS.pending;
            return (
              <TableRow key={r.transaction_id} data-testid={`referral-row-${r.transaction_id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                <TableCell><Person p={r.referrer} /></TableCell>
                <TableCell><ArrowRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                <TableCell><Person p={r.referred} /></TableCell>
                <TableCell><code className="text-xs font-mono">{r.referral_code}</code></TableCell>
                <TableCell className="text-xs capitalize">{r.plan}</TableCell>
                <TableCell className="text-xs">{r.new_subscriber_discount ? "50% off first month" : <span className="text-muted-foreground">None (before double-sided rewards)</span>}</TableCell>
                <TableCell><Badge className={s.cls} data-testid={`referral-status-${r.transaction_id}`}>{s.label}</Badge></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function ReferralsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/referrals`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  const match = (p) => !q || [p?.name, p?.email, p?.nickname].some((v) => (v || "").toLowerCase().includes(q));
  const referrers = useMemo(() => (data?.referrers || []).filter((r) => match(r) || (r.referral_code || "").toLowerCase().includes(q)), [data, q]);  // eslint-disable-line react-hooks/exhaustive-deps
  const referrals = useMemo(() => (data?.referrals || []).filter((r) => match(r.referrer) || match(r.referred) || (r.referral_code || "").toLowerCase().includes(q)), [data, q]);  // eslint-disable-line react-hooks/exhaustive-deps
  const totals = data?.totals || {};

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-6 space-y-6" data-testid="referrals-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/10"><Gift className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Referrals</h2>
            <p className="text-sm text-muted-foreground">Who referred whom, and the 50%-off months still owed. Both sides get 50% off: the referrer on their next month, the new subscriber on their first.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="referrals-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Gift} label="Rewarded referrals" value={totals.rewarded ?? "—"} testId="referrals-stat-rewarded" />
        <StatCard icon={Clock} label="Checkouts not completed" value={totals.pending ?? "—"} testId="referrals-stat-pending" />
        <StatCard icon={Users} label="Active referrers" value={totals.referrers ?? "—"} testId="referrals-stat-referrers" />
        <StatCard icon={BadgePercent} label="Outstanding 50% months" value={totals.outstanding_months ?? "—"} testId="referrals-stat-outstanding" />
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email or code" className="pl-9" data-testid="referrals-search-input" />
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading referrals...</div>
      ) : !referrals.length && !referrers.length ? (
        <p className="text-sm text-muted-foreground" data-testid="referrals-empty">{q ? "Nothing matches your search." : "No referrals yet. Student subscribers find their code on the Profile page."}</p>
      ) : (
        <>
          <section className="space-y-2">
            <h3 className="font-semibold">Referrers</h3>
            {referrers.length ? <ReferrersTable referrers={referrers} /> : <p className="text-sm text-muted-foreground">No referrers match.</p>}
          </section>
          <section className="space-y-2">
            <h3 className="font-semibold">Referral log</h3>
            {referrals.length ? <ReferralLogTable referrals={referrals} /> : <p className="text-sm text-muted-foreground">No referrals match.</p>}
          </section>
        </>
      )}
    </div>
  );
}
